import { useCallback, useRef } from 'react';

import type { IRtcStats, IVideoRoomTokenRequest, IVideoRoomTokenResponse } from 'vcs-common';
import { toast } from 'react-toastify';

import type { VideoRoomMetricsReporter, VideoRoomTokenProvider } from '@zingizi/video-room';

import { setSupportRtcStats } from '../support/supportRtcStatsStore';

type GenerateVideoRoomToken = (request: IVideoRoomTokenRequest & { as_guest?: boolean }) => {
  unwrap: () => Promise<IVideoRoomTokenResponse>;
};

// Токен LiveKit переиспользуем, пока он жив: префлайт-проверка сети и последующий вход
// в комнату запрашивают его с одинаковыми параметрами, а второй generate-token
// на ту же пару (комната, identity) сервер отдаёт уже с занятым слотом участника.
const isUsableLivekitToken = (token: string) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp > Math.floor(Date.now() / 1000) + 30;
  } catch {
    return false;
  }
};

type UploadMetrics = (report: IRtcStats) => {
  unwrap: () => Promise<void>;
};

interface UseZingiziVideoRoomPortsArgs {
  generateToken: GenerateVideoRoomToken;
  uploadMetrics: UploadMetrics;
  /**
   * Групповой участник, залогиненный под посторонним аккаунтом (например, психолог другой
   * консультации), для consultation-service не участник этой встречи — с его Authorization
   * generate-token отдаёт 403. Заходить он должен по коду комнаты, как гость.
   */
  joinAsGuest?: boolean;
}

export function useZingiziVideoRoomPorts({
  generateToken,
  uploadMetrics,
  joinAsGuest = false,
}: UseZingiziVideoRoomPortsArgs) {
  // Флаг держим в ref: generateRoomToken уходит внутрь видео-хука, и его пересоздание
  // дёргало бы переподключение к комнате.
  const joinAsGuestRef = useRef(joinAsGuest);
  joinAsGuestRef.current = joinAsGuest;
  const roomTokenCacheRef = useRef(new Map<string, IVideoRoomTokenResponse>());

  const generateRoomToken = useCallback(
    async (tokenRequest: IVideoRoomTokenRequest, silent = false): Promise<IVideoRoomTokenResponse | undefined> => {
      const cacheKey = JSON.stringify(tokenRequest);
      const cached = roomTokenCacheRef.current.get(cacheKey);
      if (cached && isUsableLivekitToken(cached.token)) return cached;
      roomTokenCacheRef.current.delete(cacheKey);

      try {
        const token = await generateToken({ ...tokenRequest, as_guest: joinAsGuestRef.current }).unwrap();
        roomTokenCacheRef.current.set(cacheKey, token);
        return token;
      } catch (error: unknown) {
        // Тихий режим — префлайт сети: свою ошибку он показывает сам, тостов не нужно.
        if (silent) return;
        console.error(
          `Failed to generate token for room ${tokenRequest.room_name}, user ${tokenRequest.user_identity}`,
          error,
        );

        if (typeof error === 'object' && error !== null && 'status' in error) {
          const status = error.status;

          if (status === 403) {
            toast.error('Консультация не активна, доступ запрещен');
            return;
          }

          if (status === 404) {
            toast.error('Консультация не найдена');
            return;
          }

          if (status === 409) {
            toast.error('В комнате уже максимальное число участников');
            return;
          }
        }

        toast.error('Произошла непредвиденная ошибка при подключении к консультации.');
      }
    },
    [generateToken],
  );

  const tokenProvider = useCallback<VideoRoomTokenProvider>(
    async (request) => {
      return generateRoomToken({
        code: String(request.metadata?.code ?? request.roomName),
        is_echo_test: Boolean(request.metadata?.isEchoTest ?? false),
        room_name: request.roomName,
        server_type: String(request.metadata?.serverType ?? 'livekit'),
        user_identity: request.participantIdentity,
      });
    },
    [generateRoomToken],
  );

  const metricsReporter = useCallback<VideoRoomMetricsReporter>(
    async (report) => {
      // Диагностика саппорт-бота читает статистику отсюда: единственная точка, где отчёт виден целиком.
      setSupportRtcStats(report);
      try {
        return await uploadMetrics(report).unwrap();
      } catch (error) {
        console.error(`Failed to upload metrics for room ${report.roomCode}, user ${report.localUserIdentity}`, error);
      }
    },
    [uploadMetrics],
  );

  return {
    generateRoomToken,
    metricsReporter,
    tokenProvider,
  };
}
